import { forwardRef, useState, type HTMLAttributes, type MouseEvent, type ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';
import { Icon, type PhxIconName } from '@/shared/ui/icon';

export type PanelVariant = 'filled' | 'outline' | 'ghost';
export type PanelTone = 'default' | 'raised';
export type PanelDensity = 'default' | 'tight';

export interface PanelProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  variant?: PanelVariant;
  tone?: PanelTone;
  icon?: PhxIconName;
  title?: ReactNode;
  subtitle?: ReactNode;
  badge?: ReactNode;
  actions?: ReactNode;
  tabs?: ReactNode;
  footer?: ReactNode;
  collapsible?: boolean;
  defaultOpen?: boolean;
  padded?: boolean;
  density?: PanelDensity;
  children?: ReactNode;
}

const VARIANT_SHELL: Record<PanelVariant, string> = {
  filled: 'bg-phx-surface border border-phx-border shadow-phx-xs',
  outline: 'bg-transparent border border-phx-border-2',
  ghost: 'bg-transparent border-0 rounded-none',
};

const HEAD_VARIANT: Record<PanelVariant, string> = {
  filled: 'bg-phx-surface-2 border-b border-phx-border',
  outline: 'bg-transparent border-b border-phx-border',
  ghost: 'bg-transparent border-b border-phx-border-2 px-0',
};

const HEAD_DENSITY: Record<PanelDensity, string> = {
  default: 'min-h-[30px] px-phx-5 py-phx-3_5',
  tight: 'min-h-phx-control px-phx-4 py-phx-2',
};

const BODY_PADDING: Record<PanelDensity, string> = {
  default: 'p-phx-5',
  tight: 'p-phx-4',
};

const GHOST_BODY_PADDING: Record<PanelDensity, string> = {
  default: 'pt-phx-4_5 pb-phx-2',
  tight: 'pt-phx-4 pb-phx-2',
};

const FOOTER_VARIANT: Record<PanelVariant, string> = {
  filled: 'bg-phx-surface-2 border-t border-phx-border',
  outline: 'bg-transparent border-t border-phx-border',
  ghost: 'bg-transparent border-t border-phx-border-2 px-0 py-phx-3',
};

export const Panel = forwardRef<HTMLElement, PanelProps>(function Panel(
  {
    variant = 'filled',
    tone,
    icon,
    title,
    subtitle,
    badge,
    actions,
    tabs,
    footer,
    collapsible,
    defaultOpen = true,
    padded = true,
    density = 'default',
    children,
    className,
    ...rest
  },
  ref
) {
  const [open, setOpen] = useState(defaultOpen);
  const hasHeader = Boolean(icon || title || subtitle || badge || actions || collapsible);
  const headerClickable = Boolean(collapsible);
  const raised = tone === 'raised';

  const radius = variant === 'ghost' ? '' : 'rounded-phx-md';
  const showFooter = footer && (!collapsible || open);

  const handleHeaderClick = headerClickable ? () => setOpen((v) => !v) : undefined;
  const handleChevronClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setOpen((v) => !v);
  };

  return (
    <section
      ref={ref}
      data-variant={variant}
      data-tone={tone}
      data-density={density}
      data-collapsed={collapsible && !open ? '' : undefined}
      className={cn(
        'phx-panel flex min-w-0 flex-col',
        VARIANT_SHELL[variant],
        radius,
        raised && variant === 'outline' && 'bg-phx-surface-2',
        className
      )}
      {...rest}
    >
      {hasHeader ? (
        <header
          onClick={handleHeaderClick}
          className={cn(
            'phx-panel-head gap-phx-4 flex items-center',
            HEAD_VARIANT[variant],
            HEAD_DENSITY[density],
            collapsible && !open && 'border-b-0',
            headerClickable && 'cursor-pointer'
          )}
        >
          {collapsible ? (
            <button
              type="button"
              aria-label={open ? 'Collapse' : 'Expand'}
              onClick={handleChevronClick}
              className="phx-panel-chev rounded-phx-sm text-phx-text-muted hover:bg-phx-surface-3 hover:text-phx-text -ml-1 inline-flex h-4.5 w-4.5 items-center justify-center border-0 bg-transparent p-0"
            >
              <Icon name={open ? 'chevdown' : 'chevright'} size={12} />
            </button>
          ) : null}
          {icon ? (
            <Icon
              name={icon}
              size={13}
              className={cn(
                'phx-panel-icon shrink-0',
                variant === 'filled' ? 'text-phx-primary' : 'text-phx-text-2'
              )}
            />
          ) : null}
          <div className="phx-panel-titlewrap gap-phx-4 flex min-w-0 flex-1 items-baseline">
            {title ? (
              <div
                className={cn(
                  'phx-panel-title text-phx-md text-phx-text truncate font-semibold tracking-[-0.005em]',
                  variant === 'ghost' &&
                    'text-phx-xs text-phx-text-2 font-semibold tracking-wider uppercase'
                )}
              >
                {title}
              </div>
            ) : null}
            {subtitle ? (
              <div className="phx-panel-sub text-phx-sm text-phx-text-muted truncate font-normal">
                {subtitle}
              </div>
            ) : null}
          </div>
          {badge ? <span className="phx-panel-badge shrink-0">{badge}</span> : null}
          {actions ? (
            <div
              onClick={(event) => event.stopPropagation()}
              className="phx-panel-actions gap-phx-2 ml-auto flex shrink-0 items-center"
            >
              {actions}
            </div>
          ) : null}
        </header>
      ) : null}
      {tabs && open ? (
        <div
          className={cn(
            'phx-panel-tabs bg-phx-surface border-phx-border border-b',
            variant === 'ghost' ? 'px-0' : 'px-phx-5'
          )}
        >
          {tabs}
        </div>
      ) : null}
      {open ? (
        <div
          data-padded={padded ? '' : undefined}
          className={cn(
            'phx-panel-body min-w-0 flex-1',
            padded && (variant === 'ghost' ? GHOST_BODY_PADDING[density] : BODY_PADDING[density])
          )}
        >
          {children}
        </div>
      ) : null}
      {showFooter ? (
        <footer
          className={cn(
            'phx-panel-foot gap-phx-4 text-phx-sm flex items-center',
            FOOTER_VARIANT[variant],
            variant !== 'ghost' && 'px-phx-5 py-phx-4'
          )}
        >
          {footer}
        </footer>
      ) : null}
    </section>
  );
});
